"use client";

import { getLqipProps } from "@/lib/utils";
import Image, { getImageProps, type ImageProps } from "next/image";
import { type ComponentProps, memo, useMemo } from "react";

interface ImageSrc {
	width?: number;
	height?: number;
	quality?: number;
	src: string;
	fill?: boolean;
	media?: string;
	loader?: ImageProps["loader"];
	style?: React.CSSProperties;
}

interface Base extends Omit<ComponentProps<"img">, "src" | "alt" | "width" | "height"> {
	alt: string;
	sizes: string;
	fill?: boolean;
	width?: number;
	height?: number;
	loader?: ImageProps["loader"];
	loading?: ImageProps["loading"];
	fetchPriority?: ImageProps["fetchPriority"];
	quality?: number;
	lqip?: string | number;
}

export interface PictureArtDirectionProps extends Base {
	media: {
		[key: string]: ImageSrc | string;
	};
	src?: string;
}

export interface PictureNormalProps extends ImageSrc, Base {
	media?: never;
}

export type PictureProps = PictureArtDirectionProps | PictureNormalProps;

export const Picture = memo(({ src, media, lqip, ...common }: PictureProps) => {
	if (typeof media !== "object") {
		return <Image {...common} src={src} />;
	}

	const firstSrcSet = useMemo(() => src || Object.values(media)[0], [media, src]);

	const {
		props: { srcSet: _, ...rest },
	} = getImageProps({
		...common,
		...(typeof firstSrcSet === "string" ? { src: firstSrcSet } : firstSrcSet),
	});

	return (
		<picture>
			{Object.entries(media).map(([media, src]) => {
				const {
					props: { srcSet },
				} = getImageProps({
					...common,
					...(typeof src === "string" ? { src } : src),
				});
				return <source key={media} srcSet={srcSet} media={media} />;
			})}
			{/* biome-ignore lint/a11y/useAltText: <explanation> */}
			<img {...getLqipProps(lqip, rest)} />
		</picture>
	);
});

Picture.displayName = "Picture";
